import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "The commonest javascript bug",
  "description": "",
  "published": "2012-02-24T00:00:00.000Z",
  "redirect_from": ["/blog/the-commonest-javascript-bug/swizec/3740"],
  "categories": "closures, For loop, JavaScript, JSON, Languages, Programming, Scope (computer science), Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/02/2921731384_72ed2e98da_m5.jpg",
        "alt": "CAEN 25th Anniversary Celebration",
        "title": "CAEN 25th Anniversary Celebration"
      }}></img></p>
    <p>{`Every couple of weeks somebody will come running to me: `}<em parentName="p">{`"Swizec, Swizec, I did everything right, but this `}<a parentName="em" {...{
          "href": "http://en.wikipedia.org/wiki/JavaScript",
          "title": "JavaScript"
        }}>{`javascript`}</a>{` isn't doing what it's supposed to! Whatever I `}{`[click]`}{`(`}<a parentName="em" {...{
          "href": "http://en.wikipedia.org/wiki/Event"
        }}>{`http://en.wikipedia.org/wiki/Event`}</a></em>{`%28computing%29 "Event (computing)") only the last item works. Halp!"`}{`_`}</p>
    <p>{`Every time it's the same bug - people just don't understand how loops and `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Closure_%28computer_science%29",
        "title": "Closure (computer science)"
      }}>{`closures`}</a>{` play together in javascript. Invariably they `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Control_flow",
        "title": "Control flow"
      }}>{`loop`}</a>{` through an array, attach the index to an event, then get frustrated when, upon triggering the event, that particular variable seems to have the wrong value.`}</p>
    <p>{`The code usually looks something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`json = JSON.parse(this.responseText);
for (i = 0; i < json.links.length; i++) {
       params = json.links[i];

       row = $$.ui.createRow({
                 // use params for settings, it works
                 click: function () {
                    var modalBrowser = new MiniBrowser({
                                        // it all breaks down here
                                        url: params.url,
                    });
       });
       // do some other stuff
}
`}</code></pre>
    <p>{`Can you spot the problem?`}</p>
    <p>{`On the first use of `}<em parentName="p">{`params`}</em>{` everything is happy, using the `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Local_variable",
        "title": "Local variable"
      }}>{`local variable`}</a>{`, which is updated on every loop iteration. But when that `}<em parentName="p">{`click`}</em>{` event is triggered it `}<strong parentName="p">{`still`}</strong>{` uses the local params variable ... that was updated on every loop iteration.`}</p>
    <p>{`By now, the loop has long since completed and that variable holds the last value - causing a weird-looking bug that is difficult for newbies to fix because, hey, why should I know about how pointers work? This is a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Scripting_language",
        "title": "Scripting language"
      }}>{`scripting language`}</a>{` damn it!`}</p>
    <p>{`Luckily the fix is very simple:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`json = JSON.parse(this.responseText);
json.links.map(function (params) {

       row = $$.ui.createRow({
               // use params for settings, it works
               click: function () {
                    var modalBrowser = new MiniBrowser({
                                   // it magically still works here
                                   url: params.url,
                    });
       });
       // do some other stuff
});
`}</code></pre>
    <p>{`Just change the for loop to a map and you don't even have to fix anything else. Everything works as usual, except now all the code is wrapped up in a closure and caries a reference to the whole `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Scope_%28computer_science%29",
        "title": "Scope (computer science)"
      }}>{`variable scope`}</a>{` around. Simply put - when the click event is triggered all the variables that were known when it was set are still there, with exactly the same values.`}</p>
    <p>{`Generally speaking, even a modest understanding of closures (like I have) will make it `}<em parentName="p">{`much`}</em>{` easier to write javascript. Might even help you fix a memory leak or fifty.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/fun-javascript-feature/swizec/3141"
        }}>{`Fun javascript feature`}</a>{` (swizec.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/9426655/how-to-disable-javascript-redirect-on-click-event"
        }}>{`How to disable Javascript redirect on click event`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/9425775/dynamic-closure-functions-for-a-multiplication-game-in-javascript"
        }}>{`dynamic closure functions for a multiplication game in javascript`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://css.dzone.com/articles/major-and-minor-javascript"
        }}>{`Major and Minor JavaScript Pitfalls and ECMAScript 6`}</a>{` (css.dzone.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/9245484/pass-php-variable-from-while-loop-to-javascript-function"
        }}>{`Pass PHP variable from while loop to javascript function`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://lostechies.com/derekgreer/2012/02/17/javascript-closures-explained/"
        }}>{`JavaScript Closures Explained`}</a>{` (lostechies.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=41f7ec3d-4d57-4679-9c01-21f8627cc5bc",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      